// Set BACKEND_URI to window.location.origin if not provided
const BACKEND_URI = typeof window !== "undefined" && window.location && window.location.origin ? window.location.origin : "";

import { ChatAppResponse, ChatAppResponseOrError, ChatAppRequest, Config, SimpleAPIResponse, HistoryListApiResponse, HistoryApiResponse } from "./models";
import { useLogin, getToken, isUsingAppServicesLogin } from "../authConfig";

export async function getHeaders(idToken: string | undefined): Promise<Record<string, string>> {
    // If using login and not using App Services login, add the id token of the logged in account as the authorization
    if (useLogin && !isUsingAppServicesLogin) {
        if (idToken) {
            return { Authorization: `Bearer ${idToken}` };
        }
    }
    return {};
}

export async function configApi(): Promise<Config> {
    const response = await fetch(`${BACKEND_URI}/config`, {
        method: "GET"
    });
    return (await response.json()) as Config;
}

export async function askApi(request: ChatAppRequest, idToken: string | undefined): Promise<ChatAppResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
    if (response.status > 299 || !response.ok) {
        throw Error(`Request failed with status ${response.status}`);
    }
    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (parsedResponse.error) {
        throw Error(parsedResponse.error);
    }
    return parsedResponse as ChatAppResponse;
}

/**
 * In our chatApi, the outgoing ChatAppRequest is stringified—
 * note that the `messages` array now contains objects whose "content"
 * property may be a dictionary with a subject and a body.
 */
export async function chatApi(request: ChatAppRequest, shouldStream: boolean, idToken: string | undefined): Promise<Response> {
    let url = `${BACKEND_URI}/chat`;
    if (shouldStream) {
        url += "/stream";
    }
    const headers = await getHeaders(idToken);
    return await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
}

let chatHistoryBlobEnabled: boolean | undefined;
async function ensureBlobHistoryEnabled() {
    if (chatHistoryBlobEnabled === undefined) {
        const config = await configApi();
        chatHistoryBlobEnabled = config.showChatHistoryBlob;
    }
    if (!chatHistoryBlobEnabled) {
        throw new Error("Blob chat history is not enabled.");
    }
}

function buildAuthHeaders(idToken?: string): Record<string, string> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (idToken) headers["Authorization"] = `Bearer ${idToken}`;
    return headers;
}

export async function postChatHistoryApi(item: any, idToken: string): Promise<any> {
    await ensureBlobHistoryEnabled();
    //console.log("postChatHistoryApi payload:", item); // DEBUG: Only uncomment for dev
    const headers = buildAuthHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/chat_history`, {
        method: "POST",
        headers,
        body: JSON.stringify(item)
    });
    if (!response.ok) {
        throw new Error(`Posting chat history failed: ${response.statusText}`);
    }
    const dataResponse: any = await response.json();
    return dataResponse;
}

export async function getChatHistoryListApi(count: number, continuationToken: string | undefined, idToken: string): Promise<HistoryListApiResponse> {
    await ensureBlobHistoryEnabled();
    const headers = buildAuthHeaders(idToken);
    const url = `${BACKEND_URI}/chat_history/sessions?count=${count}${continuationToken ? `&continuation_token=${continuationToken}` : ""}`;
    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Fetching chat history list failed: ${response.statusText}`);
    }
    return await response.json();
}

export async function getChatHistoryApi(id: string, idToken: string): Promise<HistoryApiResponse> {
    await ensureBlobHistoryEnabled();
    const headers = buildAuthHeaders(idToken);
    const url = `${BACKEND_URI}/chat_history/sessions/${id}`;
    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Fetching chat history failed: ${response.statusText}`);
    }
    return await response.json();
}

export async function deleteChatHistoryApi(id: string, idToken: string): Promise<any> {
    await ensureBlobHistoryEnabled();
    const headers = buildAuthHeaders(idToken);
    const url = `${BACKEND_URI}/chat_history/sessions/${id}`;
    const response = await fetch(url, { method: "DELETE", headers });
    if (!response.ok) {
        throw new Error(`Deleting chat history failed: ${response.statusText}`);
    }
    return await response.json();
}
